import { useQuery } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { activityApi, type ActivityEntry } from "@/api/activity";
import { Activity as ActivityIcon } from "lucide-react";

function actionLabel(action: string): string {
  return action.replace(/\./g, " ").replace(/_/g, " ");
}

function actionVariant(action: string): "success" | "warning" | "danger" | "default" {
  if (action.includes("created")) return "success";
  if (action.includes("deleted") || action.includes("removed")) return "danger";
  if (action.includes("updated") || action.includes("changed")) return "warning";
  return "default";
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function Activity() {
  const { tenantId } = useTenant();
  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["activity", tenantId],
    queryFn: () => activityApi.list(tenantId),
  });

  return (
    <CenterPanel title="Activity">
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading activity...</p>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <ActivityIcon size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No activity recorded yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-1">
          {entries.map((entry) => (
            <div key={entry.id} className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-surface-hover transition-colors">
              <div className="h-2 w-2 shrink-0 rounded-full bg-accent" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary">
                  <Badge variant={actionVariant(entry.action)} className="mr-2 text-[10px] capitalize">
                    {actionLabel(entry.action)}
                  </Badge>
                  {entry.targetType && (
                    <span className="text-text-muted">
                      {entry.targetType}
                      {entry.targetId && ` ${entry.targetId.slice(0, 8)}`}
                    </span>
                  )}
                </p>
                <p className="text-xs text-text-muted mt-0.5">
                  {entry.actorType}{entry.actorId && `: ${entry.actorId}`}
                </p>
              </div>
              <span className="shrink-0 text-xs text-text-muted">{timeAgo(entry.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
